import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { BookOpen, Users, ArrowLeft, RefreshCw, Search, X, Download, Eye, MessageSquare, AlertTriangle, Phone, Bell } from 'lucide-react';
import AbsenceAlerts from '@/components/AbsenceAlerts';
import CivicParticipationGrid from '@/components/CivicParticipationGrid';
import PrefecturaReportsSection from '@/components/PrefecturaReportsSection';
import HistorialConductaSection from '@/components/HistorialConductaSection';
import ContactoPadresSection from '@/components/ContactoPadresSection';
import usePolling from '@/hooks/usePolling';
import pollGet from '@/utils/pollGet';
import LastUpdated from '@/components/LastUpdated';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AttendanceCellRO = ({ value }) => {
  const getColor = (v) => {
    const u = (v || '').toUpperCase();
    if (u === '1') return 'bg-green-100 text-green-700 border-green-300';
    if (u === '0') return 'bg-red-100 text-red-700 border-red-300';
    if (u === 'J' || u === 'JO') return 'bg-blue-100 text-blue-700 border-blue-300';
    if (u === 'R') return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return 'bg-white text-slate-400 border-slate-200';
  };
  return (
    <div className={`w-10 h-9 mx-auto flex items-center justify-center text-xs font-bold border-2 rounded-lg ${getColor(value)}`}>
      {value || '-'}
    </div>
  );
};

const TaskCellRO = ({ value, note, onClick }) => {
  const getColor = (v) => {
    if (v === '' || v === undefined || v === null) return 'bg-white text-slate-400 border-slate-200';
    const num = parseInt(v, 10);
    if (isNaN(num)) return 'bg-white text-slate-400 border-slate-200';
    if (num >= 80) return 'bg-green-100 text-green-700 border-green-300';
    if (num >= 60) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return 'bg-red-100 text-red-700 border-red-300';
  };
  const display = value !== '' && value !== undefined && value !== null ? `${value}%` : '-';
  if (note) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`relative w-14 h-9 mx-auto flex items-center justify-center text-xs font-bold border-2 rounded-lg ${getColor(value)}`}
        title="Ver justificación"
      >
        {display}
        <span className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full bg-indigo-500 border border-white" />
      </button>
    );
  }
  return (
    <div className={`w-14 h-9 mx-auto flex items-center justify-center text-xs font-bold border-2 rounded-lg ${getColor(value)}`}>
      {display}
    </div>
  );
};

const GrupoTutoradoPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classDetail, setClassDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('alumnos');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewNote, setViewNote] = useState(null); // {text, subject, taskLabel} | null
  const [activeSection, setActiveSection] = useState('clases');
  const [grupoInfo, setGrupoInfo] = useState(null);
  const [grupoStudents, setGrupoStudents] = useState([]);
  const [pollTick, setPollTick] = useState(0);
  const [casosRiesgo, setCasosRiesgo] = useState(null);
  const [casosLoading, setCasosLoading] = useState(false);
  // Citatorio/Aviso from within the Prevención section (same flow as inside a class)
  const [prevNotif, setPrevNotif] = useState(null); // { type, classId, subject, studentName } | null
  const [prevCitatorioForm, setPrevCitatorioForm] = useState({ fecha: '', hora: '', lugar: '', motivo: '' });
  const [prevAvisoForm, setPrevAvisoForm] = useState({ mensaje: '' });
  const [prevSending, setPrevSending] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [infoRes, classesRes] = await Promise.all([
          axios.get(`${API}/teacher/grupo-tutorado`, { headers }),
          axios.get(`${API}/teacher/grupo-tutorado/clases`, { headers })
        ]);
        setGrupoInfo(infoRes.data);
        setClasses(classesRes.data);
        setLastUpdated(Date.now());
        // Log access
        axios.post(`${API}/teacher/grupo-tutorado/log-access`, {}, { headers }).catch(() => {});
        // Load students from first class for civic participation section
        if (classesRes.data.length > 0) {
          axios.get(`${API}/teacher/grupo-tutorado/clase/${classesRes.data[0].id}`, { headers })
            .then(res => setGrupoStudents(res.data.students || []))
            .catch(() => {});
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // ---- Real-time auto-refresh (read-only tutored group view) ----
  const refreshRealtime = useCallback(async () => {
    const token2 = localStorage.getItem('token');
    const h = { headers: { Authorization: `Bearer ${token2}` } };
    try {
      if (selectedClass) {
        const res = await axios.get(`${API}/teacher/grupo-tutorado/clase/${selectedClass.id}`, h);
        setClassDetail(res.data);
      } else {
        const classesRes = await axios.get(`${API}/teacher/grupo-tutorado/clases`, h);
        setClasses(classesRes.data);
        if (classesRes.data.length > 0) {
          const r = await axios.get(`${API}/teacher/grupo-tutorado/clase/${classesRes.data[0].id}`, h);
          setGrupoStudents(r.data.students || []);
        }
      }
      setPollTick((t) => t + 1);
      setLastUpdated(Date.now());
    } catch (e) {
      // Ignore transient poll errors on purpose.
    }
  }, [selectedClass]);

  usePolling(refreshRealtime, { intervalMs: 0 });

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshRealtime();
      if (activeSection === 'prevencion') await fetchCasosRiesgo(false);
    } finally {
      setRefreshing(false);
    }
  };

  // ---- Preventive summary (at-risk students) auto-loaded & auto-refreshed ----
  const fetchCasosRiesgo = useCallback(async (silent) => {
    if (!silent) setCasosLoading(true);
    const token2 = localStorage.getItem('token');
    try {
      const res = await pollGet(`${API}/teacher/grupo-tutorado/casos-riesgo`, {
        key: 'casos-riesgo', conditional: !!silent, headers: { Authorization: `Bearer ${token2}` }
      });
      if (!res.notModified) setCasosRiesgo(res.data);
    } catch (e) {
      // Ignore transient errors on purpose.
    } finally {
      if (!silent) setCasosLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeSection === 'prevencion') fetchCasosRiesgo(false);
  }, [activeSection, fetchCasosRiesgo]);

  useEffect(() => {
    if (activeSection === 'prevencion' && pollTick > 0) fetchCasosRiesgo(true);
  }, [pollTick, activeSection, fetchCasosRiesgo]);

  const openPrevNotif = (type, classId, subject, studentName) => {
    setPrevCitatorioForm({ fecha: '', hora: '', lugar: '', motivo: '' });
    setPrevAvisoForm({ mensaje: '' });
    setPrevNotif({ type, classId, subject, studentName });
  };

  const closePrevNotif = () => setPrevNotif(null);

  const sendPrevCitatorio = async () => {
    const f = prevCitatorioForm;
    if (!f.fecha || !f.hora || !f.lugar || !f.motivo) { toast.error('Todos los campos son requeridos'); return; }
    setPrevSending(true);
    try {
      const token2 = localStorage.getItem('token');
      await axios.post(`${API}/teacher/notifications/citatorio`, {
        class_id: prevNotif.classId,
        student_names: [prevNotif.studentName],
        ...f
      }, { headers: { Authorization: `Bearer ${token2}` } });
      toast.success('Citatorio enviado correctamente');
      closePrevNotif();
    } catch (e) {
      toast.error('Error al enviar el citatorio');
    } finally {
      setPrevSending(false);
    }
  };

  const sendPrevAviso = async () => {
    if (!prevAvisoForm.mensaje.trim()) { toast.error('El mensaje del aviso es requerido'); return; }
    setPrevSending(true);
    try {
      const token2 = localStorage.getItem('token');
      await axios.post(`${API}/teacher/notifications/aviso`, {
        class_id: prevNotif.classId,
        student_names: [prevNotif.studentName],
        mensaje: prevAvisoForm.mensaje
      }, { headers: { Authorization: `Bearer ${token2}` } });
      toast.success('Aviso enviado correctamente');
      closePrevNotif();
    } catch (e) {
      toast.error('Error al enviar el aviso');
    } finally {
      setPrevSending(false);
    }
  };

  const openClass = async (cls) => {
    setSelectedClass(cls);
    setDetailLoading(true);
    setActiveTab('alumnos');
    setSearchTerm('');
    try {
      const res = await axios.get(`${API}/teacher/grupo-tutorado/clase/${cls.id}`, { headers });
      setClassDetail(res.data);
    } catch (error) {
      toast.error('Error al cargar la clase');
    } finally {
      setDetailLoading(false);
    }
  };

  const backToClasses = () => {
    setSelectedClass(null);
    setClassDetail(null);
  };

  if (loading) return (
    <Layout user={user} onLogout={onLogout}>
      <div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 animate-spin text-teal-500" /></div>
    </Layout>
  );

  if (selectedClass && classDetail) {
    const students = classDetail.students || [];
    const attGrid = classDetail.attendance_grid || { columns: [], records: {} };
    const taskGrid = classDetail.task_grid || { columns: [], records: {} };
    const filteredStudents = students.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()));

    const buildAttCum = (cols) => {
      const result = []; let pi = 0; let block = [];
      for (const col of cols) {
        if (col.isSeparator) { pi++; result.push({ _isCum: true, _pi: pi, _ids: block.map(c => c.id || c.date), label: `% P${pi}` }); result.push(col); block = []; }
        else { result.push(col); block.push(col); }
      }
      if (block.length > 0) { pi++; result.push({ _isCum: true, _pi: pi, _ids: block.map(c => c.id || c.date), label: cols.some(c => c.isSeparator) ? `% P${pi}` : '% Total' }); }
      return result;
    };

    const buildTaskCum = (cols) => {
      const result = []; let pi = 0; let block = [];
      for (const col of cols) {
        if (col.isSeparator) { pi++; result.push({ _isCum: true, _pi: pi, _ids: block.map(c => c.id), label: `% P${pi}` }); result.push(col); block = []; }
        else { result.push(col); block.push(col); }
      }
      if (block.length > 0) { pi++; result.push({ _isCum: true, _pi: pi, _ids: block.map(c => c.id), label: cols.some(c => c.isSeparator) ? `% P${pi}` : '% Total' }); }
      return result;
    };

    const calcAttPct = (rec, ids) => {
      let reg = 0, abs = 0;
      ids.forEach(id => { const v = (rec[id] || '').toUpperCase(); if (v !== '') { reg++; if (v === '0') abs++; } });
      return reg === 0 ? null : Math.round(((reg - abs) / reg) * 100);
    };

    const calcTaskAvg = (rec, ids) => {
      const vals = ids.map(id => rec[id]).filter(v => v !== undefined && v !== null && v !== '');
      if (vals.length === 0) return null;
      return Math.round(vals.reduce((a, v) => a + (parseInt(v, 10) || 0), 0) / vals.length);
    };

    const attDisplayCols = buildAttCum(attGrid.columns);
    const taskDisplayCols = buildTaskCum(taskGrid.columns);

    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-4 md:p-6 lg:p-8">
          <Button variant="outline" onClick={backToClasses} className="mb-4" data-testid="gt-back-to-classes">
            <ArrowLeft className="w-4 h-4 mr-2" /> Volver a Clases
          </Button>

          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-1">{selectedClass.subject}</h1>
          <p className="text-sm text-slate-600 mb-4">Grupo: {selectedClass.group} | Prof. {selectedClass.teacher_name}</p>

          <div className="flex gap-0 mb-4">
            {['alumnos', 'asistencias', 'tareas', 'participacion'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${activeTab === tab ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                data-testid={`gt-tab-${tab}`}>
                {tab === 'alumnos' ? 'Alumnos' : tab === 'asistencias' ? 'Asistencias' : tab === 'tareas' ? 'Tareas' : 'Participación'}
              </button>
            ))}
          </div>

          {activeTab !== 'alumnos' && (
            <div className="relative mb-4 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Buscar alumno..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" />
              {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2"><X className="w-4 h-4 text-slate-400" /></button>}
            </div>
          )}

          <Card className="p-4">
            {detailLoading ? (
              <div className="text-center py-12"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-teal-500" /></div>
            ) : activeTab === 'alumnos' ? (
              <div>
                <h2 className="text-lg font-semibold mb-3">Lista de Alumnos ({students.length})</h2>
                {students.length === 0 ? <p className="text-slate-500 text-center py-8">Sin alumnos</p> : (
                  <div className="space-y-1">
                    {students.map((s, i) => (
                      <div key={s.id} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50">
                        <span className="text-sm text-slate-400 w-6 text-right">{i + 1}.</span>
                        <span className="text-sm font-medium">{s.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : activeTab === 'asistencias' ? (
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <h2 className="text-lg font-semibold">Registro de Asistencias</h2>
                  <div className="flex gap-1 text-xs flex-wrap">
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">1=Asist.</span>
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded">0=Falta</span>
                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">R=Retardo</span>
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">J/JO=Justif.</span>
                  </div>
                </div>
                {attGrid.columns.length === 0 ? <p className="text-slate-500 text-center py-8">Sin registros</p> : (
                  <div className="overflow-x-auto">
                    <table className="border-collapse min-w-max">
                      <thead className="grid-sticky-header sticky top-0 z-20">
                        <tr className="border-b-2 border-slate-300 bg-slate-50">
                          <th className="sticky left-0 z-30 bg-slate-50 text-left py-2 px-2 text-xs font-semibold border-r-2 border-slate-300 min-w-[160px] max-w-[160px]">Alumno</th>
                          {attDisplayCols.map((col, i) => (
                            col._isCum ? <th key={`ac${i}`} className="text-center py-2 px-1 text-xs font-bold text-blue-700 bg-blue-50 border-r min-w-[55px]">{col.label}</th>
                            : col.isSeparator ? <th key={i} className="text-center py-1 px-0 text-[8px] font-bold text-purple-600 border-r min-w-[28px] max-w-[28px] bg-purple-100">{col.label}</th>
                            : <th key={i} className="text-center py-2 px-1 text-xs border-r min-w-[44px] bg-slate-50">{col.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudents.map((s, si) => {
                          const rec = attGrid.records[s.id] || {};
                          return (
                            <tr key={si} className="border-b hover:bg-slate-50">
                              <td className="sticky left-0 z-10 bg-white py-1 px-2 text-xs font-medium border-r-2 border-slate-300 min-w-[160px] max-w-[160px]">{s.name}</td>
                              {attDisplayCols.map((col, ci) => {
                                if (col._isCum) {
                                  const pct = calcAttPct(rec, col._ids);
                                  const clr = pct === null ? 'text-slate-400' : pct >= 80 ? 'text-green-700 bg-green-50' : pct >= 60 ? 'text-yellow-700 bg-yellow-50' : 'text-red-700 bg-red-50';
                                  return <td key={`ac${ci}`} className={`text-center py-1 px-1 border-r text-xs font-bold ${clr}`}>{pct !== null ? `${pct}%` : '-'}</td>;
                                }
                                if (col.isSeparator) return <td key={ci} className="border-r min-w-[28px] bg-purple-50"></td>;
                                const v = rec[col.id || col.date] || '';
                                return <td key={ci} className="text-center py-1 px-0 border-r"><AttendanceCellRO value={v} /></td>;
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : activeTab === 'tareas' ? (
              <><div>
                <div className="flex items-center gap-3 mb-3">
                  <h2 className="text-lg font-semibold">Registro de Tareas</h2>
                  <div className="flex gap-1 text-xs flex-wrap">
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">80-100%</span>
                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">60-79%</span>
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded">0-59%</span>
                  </div>
                </div>
                {taskGrid.columns.length === 0 ? <p className="text-slate-500 text-center py-8">Sin tareas</p> : (
                  <div className="overflow-x-auto">
                    <table className="border-collapse min-w-max">
                      <thead className="grid-sticky-header sticky top-0 z-20">
                        <tr className="border-b-2 border-slate-300 bg-slate-50">
                          <th className="sticky left-0 z-30 bg-slate-50 text-left py-2 px-2 text-xs font-semibold border-r-2 border-slate-300 min-w-[160px] max-w-[160px]">Alumno</th>
                          {taskDisplayCols.map((col, i) => (
                            col._isCum ? <th key={`tc${i}`} className="text-center py-2 px-1 text-xs font-bold text-blue-700 bg-blue-50 border-r min-w-[55px]">{col.label}</th>
                            : col.isSeparator ? <th key={i} className="text-center py-1 px-0 text-[8px] font-bold text-purple-600 border-r min-w-[28px] max-w-[28px] bg-purple-100">{col.label}</th>
                            : <th key={i} className="text-center py-2 px-1 text-xs border-r min-w-[55px] bg-slate-50">
                                <div className="font-bold">{col.number}</div>
                                <div className="text-slate-500 text-[10px]">{col.date ? col.date.split('-').reverse().slice(0, 2).join('/') : ''}</div>
                              </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudents.map((s, si) => {
                          const rec = taskGrid.records[s.id] || {};
                          return (
                            <tr key={si} className="border-b hover:bg-slate-50">
                              <td className="sticky left-0 z-10 bg-white py-1 px-2 text-xs font-medium border-r-2 border-slate-300 min-w-[160px] max-w-[160px]">{s.name}</td>
                              {taskDisplayCols.map((col, ci) => {
                                if (col._isCum) {
                                  const avg = calcTaskAvg(rec, col._ids);
                                  const clr = avg === null ? 'text-slate-400' : avg >= 80 ? 'text-green-700 bg-green-50' : avg >= 60 ? 'text-yellow-700 bg-yellow-50' : 'text-red-700 bg-red-50';
                                  return <td key={`tc${ci}`} className={`text-center py-1 px-1 border-r text-xs font-bold ${clr}`}>{avg !== null ? `${avg}%` : '-'}</td>;
                                }
                                if (col.isSeparator) return <td key={ci} className="border-r min-w-[28px] bg-purple-50"></td>;
                                const noteText = (taskGrid.notes || {})[s.id]?.[col.id];
                                return <td key={ci} className="text-center py-1 px-0 border-r"><TaskCellRO value={rec[col.id] || ''} note={noteText} onClick={() => setViewNote({ text: noteText, subject: selectedClass.subject, taskLabel: col.description || `Tarea ${col.number || ''}` })} /></td>;
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {(() => {
                const tw = taskGrid.task_weights || {};
                const cols = taskGrid.columns;
                const hasSep = cols.some(c => c.isSeparator);
                const blks = [];
                let curBlk = [];
                let pi = 0;
                for (const col of cols) {
                  if (col.isSeparator) { pi++; blks.push({ parcial: pi, taskIds: curBlk.map(c => c.id), label: `Parcial ${pi}` }); curBlk = []; }
                  else { curBlk.push(col); }
                }
                if (curBlk.length > 0) { pi++; blks.push({ parcial: pi, taskIds: curBlk.map(c => c.id), label: hasSep ? `Parcial ${pi}` : 'General' }); }
                const pKeys = ['parcial_1', 'parcial_2', 'parcial_3'];
                const hasW = pKeys.some(k => tw[k] && tw[k] !== '');
                if (!hasW || blks.length === 0) return null;
                return (
                  <div className="mt-4 border-t-2 border-indigo-100 pt-3">
                    <h3 className="text-base font-semibold text-indigo-700 mb-2">Calificacion por Tareas (sobre 10)</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead className="grid-sticky-header sticky top-0 z-20">
                          <tr className="border-b-2 border-indigo-200 bg-indigo-50">
                            <th className="sticky left-0 top-0 z-30 bg-indigo-50 text-left py-2 px-3 font-semibold text-indigo-700 min-w-[140px]">Alumno</th>
                            {blks.map((b, i) => {
                              const w = (parseFloat(tw[pKeys[i]]) || 0);
                              if (w <= 0) return null;
                              return <th key={i} className="text-center py-2 px-2 text-xs font-semibold text-indigo-700">{b.label} ({w}%)</th>;
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {students.map(s => {
                            const rec = taskGrid.records[s.id] || {};
                            return (
                              <tr key={s.id} className="border-b hover:bg-slate-50">
                                <td className="py-1.5 px-3 font-medium text-slate-900">{s.name}</td>
                                {blks.map((b, i) => {
                                  const w = (parseFloat(tw[pKeys[i]]) || 0);
                                  if (w <= 0) return null;
                                  const vals = b.taskIds.map(id => rec[id]).filter(v => v !== undefined && v !== null && v !== '');
                                  if (vals.length === 0) return <td key={i} className="text-center py-1.5 px-2 text-slate-400">--</td>;
                                  const avg = vals.reduce((a, v) => a + (parseInt(v, 10) || 0), 0) / vals.length;
                                  const grade = (avg / 100) * (w / 100) * 10;
                                  const clr = grade >= (w / 100 * 8) ? 'text-green-700 bg-green-50' : grade >= (w / 100 * 6) ? 'text-yellow-700 bg-yellow-50' : 'text-red-700 bg-red-50';
                                  return <td key={i} className={`text-center py-1.5 px-2 font-bold ${clr} rounded`}>{grade.toFixed(2)}</td>;
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
              </>
            ) : activeTab === 'participacion' ? (
              <CivicParticipationGrid
                classId={selectedClass.id}
                students={classDetail.students || []}
                role="teacher"
                isTutor={true}
                orientadorJustifications={[]}
                refreshTick={pollTick}
              />
            ) : null}

          </Card>
        </div>

        {/* Read-only task note viewer */}
        <Dialog open={!!viewNote} onOpenChange={(open) => { if (!open) setViewNote(null); }}>
          <DialogContent className="max-w-md" data-testid="gt-note-view-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-indigo-600" />
                Justificación de la calificación
              </DialogTitle>
            </DialogHeader>
            {viewNote && (
              <div className="space-y-2">
                <p className="text-xs text-slate-500">{viewNote.subject} — {viewNote.taskLabel}</p>
                <p className="text-sm text-slate-800 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap">{viewNote.text}</p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </Layout>
    );
  }

  const downloadExcel = async () => {
    try {
      const res = await axios.get(`${API}/teacher/grupo-tutorado/export-excel`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Reporte_Grupo_Tutorado.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Reporte descargado');
    } catch (e) { toast.error('Error al descargar reporte'); }
  };

  const downloadGradesExcel = async () => {
    try {
      const res = await axios.get(`${API}/teacher/grupo-tutorado/export-grades-excel`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Concentrado_Calificaciones.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Concentrado de calificaciones descargado');
    } catch (e) { toast.error('Error al descargar el concentrado'); }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6 lg:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-1" data-testid="gt-dashboard-title">Grupo Tutorado</h1>
            <p className="text-sm text-slate-600">
              {grupoInfo?.group_name ? `Grupo: ${grupoInfo.group_name}` : 'Visualizacion de clases de tu grupo tutorado'}
            </p>
            <div className="mt-2">
              <LastUpdated timestamp={lastUpdated} onRefresh={handleRefresh} refreshing={refreshing} />
            </div>
          </div>
          {activeSection === 'clases' && classes.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => navigate('/teacher/grupo-tutorado/reporte?type=attendance')} variant="outline" className="border-emerald-500 text-emerald-700 hover:bg-emerald-50" data-testid="gt-view-attendance-btn">
                <Eye className="w-4 h-4 mr-2" /> Ver Asistencias/Tareas
              </Button>
              <Button onClick={downloadExcel} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="gt-download-excel">
                <Download className="w-4 h-4 mr-2" /> Descargar Excel
              </Button>
              <Button onClick={() => navigate('/teacher/grupo-tutorado/reporte?type=grades')} variant="outline" className="border-indigo-500 text-indigo-700 hover:bg-indigo-50" data-testid="gt-view-grades-btn">
                <Eye className="w-4 h-4 mr-2" /> Ver Calificaciones
              </Button>
              <Button onClick={downloadGradesExcel} className="bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="gt-download-grades-excel">
                <Download className="w-4 h-4 mr-2" /> Concentrado de Calificaciones
              </Button>
            </div>
          )}
        </div>

        {/* Section tabs */}
        <div className="flex gap-0 mb-6">
          <button onClick={() => setActiveSection('clases')}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-l-lg transition-all ${activeSection === 'clases' ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid="gt-section-clases">
            Clases
          </button>
          <button onClick={() => setActiveSection('participacion')}
            className={`flex-1 py-2.5 text-sm font-semibold transition-all ${activeSection === 'participacion' ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid="gt-section-participacion">
            Participación
          </button>
          <button onClick={() => setActiveSection('casos')}
            className={`flex-1 py-2.5 text-sm font-semibold transition-all ${activeSection === 'casos' ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid="gt-section-casos">
            Casos Especiales
          </button>
          <button onClick={() => setActiveSection('prevencion')}
            className={`flex-1 py-2.5 text-sm font-semibold transition-all ${activeSection === 'prevencion' ? 'bg-rose-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid="gt-section-prevencion">
            Prevención
          </button>
          <button onClick={() => setActiveSection('conducta')}
            className={`flex-1 py-2.5 text-sm font-semibold transition-all ${activeSection === 'conducta' ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid="gt-section-conducta">
            Historial Conducta
          </button>
          <button onClick={() => setActiveSection('contacto')}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-r-lg transition-all ${activeSection === 'contacto' ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid="gt-section-contacto">
            Contacto Padres
          </button>
        </div>

        {activeSection === 'contacto' ? (
          <ContactoPadresSection showHeader={false} />
        ) : activeSection === 'conducta' ? (
          <HistorialConductaSection showHeader={false} />
        ) : activeSection === 'prevencion' ? (
          <div data-testid="gt-prevencion-section">
            <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-rose-500 mt-0.5 shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">Focos Rojos del Grupo</h3>
                  <p className="text-sm text-slate-600">Alumnos con al menos una falta o alguna tarea calificada con 50 o menos, por materia. Se actualiza solo conforme los maestros cargan datos.</p>
                </div>
              </div>
              <Button onClick={() => fetchCasosRiesgo(false)} variant="outline" size="sm" className="border-rose-300 text-rose-600 hover:bg-rose-50" data-testid="gt-prevencion-refresh">
                <RefreshCw className={`w-4 h-4 mr-2 ${casosLoading ? 'animate-spin' : ''}`} /> Actualizar
              </Button>
            </div>

            {casosLoading && !casosRiesgo ? (
              <div className="flex items-center justify-center h-40"><RefreshCw className="w-7 h-7 animate-spin text-rose-500" /></div>
            ) : !casosRiesgo || casosRiesgo.classes.length === 0 ? (
              <Card className="p-8 text-center"><p className="text-slate-500">Sin materias registradas en el grupo</p></Card>
            ) : (
              <div className="space-y-6">
                {casosRiesgo.classes.map((cls, ci) => (
                  <Card key={ci} className="overflow-hidden" data-testid={`gt-prevencion-materia-${ci}`}>
                    <div className="flex items-center justify-between bg-slate-800 text-white px-4 py-3">
                      <div>
                        <h4 className="font-semibold">{cls.subject}</h4>
                        <p className="text-xs text-slate-300">Prof. {cls.teacher_name}</p>
                      </div>
                      <div className="text-right text-xs">
                        <p className="font-semibold text-rose-300">{cls.num_at_risk} en riesgo</p>
                        <p className="text-slate-300">Asistencias registradas: {cls.total_attendance}</p>
                      </div>
                    </div>
                    {cls.students.length === 0 ? (
                      <div className="px-4 py-5 text-sm text-emerald-600 bg-emerald-50">Sin alumnos en riesgo en esta materia 👌</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-slate-100 text-slate-600 text-left">
                              <th className="py-2 px-3 font-semibold">Alumno</th>
                              <th className="py-2 px-3 font-semibold text-center whitespace-nowrap">Faltas</th>
                              <th className="py-2 px-3 font-semibold">Tareas reprobadas (≤50)</th>
                              <th className="py-2 px-3 font-semibold whitespace-nowrap">Tel. Padre/Tutor</th>
                              <th className="py-2 px-3 font-semibold text-center whitespace-nowrap">Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cls.students.map((st, si) => (
                              <tr key={si} className="border-t border-slate-100 align-top" data-testid={`gt-prevencion-row-${ci}-${si}`}>
                                <td className="py-2 px-3">
                                  <p className="font-medium text-slate-800">{st.name}</p>
                                  {st.matricula && <p className="text-xs text-slate-400">{st.matricula}</p>}
                                </td>
                                <td className="py-2 px-3 text-center">
                                  {st.faltas > 0 ? (
                                    <span className="inline-flex items-center justify-center min-w-[2.5rem] font-bold text-rose-700 bg-rose-100 border border-rose-200 rounded-lg px-2 py-0.5">
                                      {st.faltas}<span className="text-[10px] font-normal text-rose-400 ml-0.5">/{st.total_attendance}</span>
                                    </span>
                                  ) : (
                                    <span className="text-slate-300">—</span>
                                  )}
                                </td>
                                <td className="py-2 px-3">
                                  {st.failed_tasks.length === 0 ? (
                                    <span className="text-slate-300">—</span>
                                  ) : (
                                    <div className="flex flex-wrap gap-1.5">
                                      {st.failed_tasks.map((t, ti) => (
                                        <span key={ti} className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-2 py-0.5 text-xs" title={`${t.description || t.label}${t.date ? ' · ' + t.date : ''}`}>
                                          {t.label}<strong className="text-rose-600">{t.value}%</strong>
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </td>
                                <td className="py-2 px-3 whitespace-nowrap">
                                  {st.celular ? (
                                    <a href={`tel:${st.celular}`} className="inline-flex items-center gap-1 text-teal-600 hover:text-teal-800 font-medium" data-testid={`gt-prevencion-call-${ci}-${si}`}>
                                      <Phone className="w-3.5 h-3.5" /> {st.celular}
                                    </a>
                                  ) : (
                                    <span className="text-slate-300">Sin número</span>
                                  )}
                                </td>
                                <td className="py-2 px-3 whitespace-nowrap">
                                  <div className="flex items-center justify-center gap-1.5">
                                    <Button size="sm" onClick={() => openPrevNotif('citatorio', cls.class_id, cls.subject, st.name)}
                                      className="bg-rose-500 hover:bg-rose-600 text-white h-7 px-2 text-xs" data-testid={`gt-prevencion-citatorio-${ci}-${si}`}>
                                      <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Citatorio
                                    </Button>
                                    <Button size="sm" onClick={() => openPrevNotif('aviso', cls.class_id, cls.subject, st.name)}
                                      className="bg-blue-500 hover:bg-blue-600 text-white h-7 px-2 text-xs" data-testid={`gt-prevencion-aviso-${ci}-${si}`}>
                                      <Bell className="w-3.5 h-3.5 mr-1" /> Aviso
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>
        ) : activeSection === 'casos' ? (
          <>
            <PrefecturaReportsSection defaultOpen={true} showStatus canDelete />
            <AbsenceAlerts showCheckbox={true} title="Casos Especiales" />
          </>
        ) : activeSection === 'participacion' ? (
          classes.length > 0 ? (
            <CivicParticipationGrid
              classId={classes[0].id}
              students={grupoStudents}
              role="teacher"
              isTutor={true}
              tutorOnly={true}
              orientadorJustifications={[]}
              refreshTick={pollTick}
            />
          ) : (
            <Card className="p-8 text-center"><p className="text-slate-500">Sin clases en el grupo</p></Card>
          )
        ) : classes.length === 0 ? (
          <Card className="p-12 text-center">
            <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">Sin clases registradas</h3>
            <p className="text-slate-600">Aun no hay clases registradas para tu grupo tutorado</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {classes.map(cls => (
              <Card key={cls.id} onClick={() => openClass(cls)}
                className="p-6 bg-white border border-slate-100 rounded-xl shadow-sm hover:shadow-md hover:border-teal-200 transition-all cursor-pointer group"
                data-testid={`gt-class-card-${cls.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-gradient-to-br from-teal-50 to-slate-100 w-12 h-12 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-teal-500" />
                  </div>
                  <span className="text-xs font-semibold text-slate-700 bg-slate-50 border px-2.5 py-0.5 rounded-full">{cls.group}</span>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-1 group-hover:text-teal-500 transition-colors">{cls.subject}</h3>
                <p className="text-sm text-slate-600 mb-3">Prof. {cls.teacher_name}</p>
                <div className="flex items-center text-sm text-slate-600">
                  <Users className="w-4 h-4 mr-1" /> {cls.student_ids?.length || 0} alumnos
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Prevención: Citatorio dialog */}
      <Dialog open={prevNotif?.type === 'citatorio'} onOpenChange={() => closePrevNotif()}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-500" /> Enviar Citatorio
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mb-2">
            {prevNotif?.subject} · Se enviará a: <span className="font-medium">{prevNotif?.studentName}</span>
          </p>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Fecha</Label>
                <Input type="date" value={prevCitatorioForm.fecha} onChange={(e) => setPrevCitatorioForm({ ...prevCitatorioForm, fecha: e.target.value })} data-testid="prev-citatorio-fecha" />
              </div>
              <div>
                <Label>Hora</Label>
                <Input type="time" value={prevCitatorioForm.hora} onChange={(e) => setPrevCitatorioForm({ ...prevCitatorioForm, hora: e.target.value })} data-testid="prev-citatorio-hora" />
              </div>
            </div>
            <div>
              <Label>Lugar</Label>
              <Input value={prevCitatorioForm.lugar} onChange={(e) => setPrevCitatorioForm({ ...prevCitatorioForm, lugar: e.target.value })} placeholder="Ej: Salón 101" data-testid="prev-citatorio-lugar" />
            </div>
            <div>
              <Label>Motivo</Label>
              <Input value={prevCitatorioForm.motivo} onChange={(e) => setPrevCitatorioForm({ ...prevCitatorioForm, motivo: e.target.value })} placeholder="Motivo del citatorio..." data-testid="prev-citatorio-motivo" />
            </div>
            <Button onClick={sendPrevCitatorio} disabled={prevSending} className="w-full bg-rose-500 hover:bg-rose-600 text-white" data-testid="prev-send-citatorio">
              {prevSending ? 'Enviando...' : 'Enviar Citatorio'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Prevención: Aviso dialog */}
      <Dialog open={prevNotif?.type === 'aviso'} onOpenChange={() => closePrevNotif()}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-blue-500" /> Enviar Aviso
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mb-2">
            {prevNotif?.subject} · Se enviará a: <span className="font-medium">{prevNotif?.studentName}</span>
          </p>
          <div className="space-y-3">
            <div>
              <Label>Mensaje</Label>
              <Input value={prevAvisoForm.mensaje} onChange={(e) => setPrevAvisoForm({ ...prevAvisoForm, mensaje: e.target.value })} placeholder="Escribe el aviso..." data-testid="prev-aviso-mensaje" />
            </div>
            <Button onClick={sendPrevAviso} disabled={prevSending} className="w-full bg-blue-500 hover:bg-blue-600 text-white" data-testid="prev-send-aviso">
              {prevSending ? 'Enviando...' : 'Enviar Aviso'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default GrupoTutoradoPage;
